import { useSelector } from "react-redux";
import { RootState } from "@store/store.tsx";
import { TotalPriceItemCart } from '@eshop/types/cart.types.ts';
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Divider, Drawer, Layout, List, Popconfirm } from "antd";
import { DeleteOutlined, ShoppingCartOutlined } from "@ant-design/icons";
import { getCartDrawerItems } from "@eshop/components/cart/cartUtils.tsx";
import { CartTotals, VoucherSection } from "@eshop/pages/cart/Bloques.tsx";
import { CartTourSteps } from "@eshop/components/tours/catalogueTours.tsx";
import { RenderTour } from "@eshop/components/tours/renderTour.tsx";
import { useNavigate } from 'react-router-dom';
import { CART_EVENTS } from '@eshop/utils/cartEvents.ts';
import { useCheckoutLayout } from '@eshop/hooks/useCheckoutLayout.tsx';
import { useClearCart } from '@eshop/hooks/useClearCart.ts';

interface CartMenuProps {
    /**
     * Forma del botón que dispara el carrito. Por defecto es "circle" para
     * mantener la apariencia actual en el AdminLayout.
     */
    shape?: 'default' | 'circle' | 'round';
    /**
     * Color del ícono del carrito. Cada template del catálogo podrá definir
     * un color distinto para personalizar su apariencia.
     */
    iconColor?: string;
    /** When true the header hides the cart affordance, but the drawer still registers for cleanup. */
    hidden?: boolean;
}

export const CartMenu: React.FC<CartMenuProps> = ({ shape = 'circle', iconColor, hidden = false }) => {
    const cartItems = useSelector((state: RootState) => state.cart.cartItems);
    const totalsCart: TotalPriceItemCart = useSelector((state: RootState) => state.cart.total);
    const [currency, setCurrency] = useState<string | undefined>();

    const navigate = useNavigate();
    const { hideNavigation, registerDrawerCloser } = useCheckoutLayout();
    const { clearCart, isClearing } = useClearCart();
    const suppressCart = hidden || hideNavigation;

    // Calcular la cantidad total de ítems en el carrito
    const itemCount = cartItems.length;

    const [openDrawer, setOpenDrawer] = useState(false);
    const [openPopConfirm, setOpenPopConfirm] = useState(false);
    const [openPopConfirmItemDelete, setOpenPopConfirmItemDelete] = useState<number | null>(null);

    const showDrawer = useCallback(() => {
        if (suppressCart) {
            return;
        }
        setOpenDrawer(true);
    }, [suppressCart]);

    const onClose = useCallback(() => {
        setOpenDrawer(false);
        setOpenPopConfirm(false);
        setOpenPopConfirmItemDelete(null);
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        const handler = () => onClose();
        window.addEventListener(CART_EVENTS.CLOSE_DRAWER, handler);
        return () => {
            window.removeEventListener(CART_EVENTS.CLOSE_DRAWER, handler);
        };
    }, [onClose]);

    useEffect(() => registerDrawerCloser(onClose), [registerDrawerCloser, onClose]);

    useEffect(() => {
        if (suppressCart) {
            onClose();
        }
    }, [suppressCart, onClose]);

    const dontEmptyCart = cartItems.length > 0;
    const containerStyle = useMemo(() => suppressCart ? { display: 'none' } : undefined, [suppressCart]);

    const handleClearCart = useCallback(async () => {
        setOpenPopConfirm(false);
        const result = await clearCart();
        if (!result || !result.success) {
            setOpenDrawer(true);
        }
    }, [clearCart]);

    return (
        <div className="w-auto bg-transparent" style={containerStyle} aria-hidden={suppressCart}>
            {dontEmptyCart &&
                <RenderTour tourSteps={CartTourSteps} tourName={'Tour por Carrito'}/>
            }
            <Badge count={itemCount}>
                <Button
                    id="idCartButton"
                    className="cartButtonClass"
                    shape={shape}
                    disabled={suppressCart}
                    icon={<ShoppingCartOutlined style={iconColor ? { color: iconColor } : undefined}/>}
                    onClick={showDrawer}
                />
            </Badge>

            <Drawer
                title="Carrito de Compras"
                placement="right"
                closable={true}
                onClose={onClose}
                open={openDrawer && !suppressCart}
            >
                {dontEmptyCart ?
                    <>
                        <div className="w-100 d-flex">
                            <a className="w-50 mx-auto fw-bold text-info text-center"
                               style={{textDecorationLine: "underline!important"}}
                               href={'/eshop/cart'}>
                                Ir al Carrito
                            </a>
                        </div>
                        <div className="pt-2 mt-2 w_300px ml-auto ms-auto">
                            {/* Reemplaza la lógica de los vouchers con el nuevo componente */}
                            <VoucherSection/>
                        </div>
                    </>
                    : ''}

                <List
                    itemLayout="horizontal"
                    dataSource={getCartDrawerItems(
                        cartItems,
                        currency,
                        setCurrency,
                        openPopConfirmItemDelete,
                        setOpenPopConfirmItemDelete,
                    )}
                    className="cartItemListClass"
                    renderItem={item => (
                        <List.Item key={item.key}>
                            <List.Item.Meta
                                avatar={item.imageElement}
                                title={item.title}
                                description={item.description}
                            />
                        </List.Item>
                    )}
                />
                {dontEmptyCart ?
                    <Layout className="bg-transparent">
                        <CartTotals total={totalsCart} currency={currency}/>
                    </Layout> : ''}
                <Divider/>
                {dontEmptyCart && (
                    <Button className="checkoutBtnClass w-100 border border-primary text-primary"
                            key="Button-Pagar"
                            block
                        // shape="round"
                        //     size='medium'
                            onClick={() => (navigate('/eshop/checkout'))}>
                        Pagar
                    </Button>)}
                {dontEmptyCart && (
                    <div className="w-100 d-flex justify-content-center my-4">
                        <Popconfirm
                            key={'clean_confirm'}
                            title="¿Estás seguro de que deseas vaciar el carrito?"
                            onConfirm={handleClearCart}
                            onCancel={() => setOpenPopConfirm(false)}
                            okText="Sí"
                            cancelText="No"
                            open={openPopConfirm}
                            disabled={isClearing}
                            okButtonProps={{ loading: isClearing }}
                        >
                            <Button key={'clean'}
                                    onClick={() => {
                                        if (!isClearing) {
                                            setOpenPopConfirm(true);
                                        }
                                    }}
                                    color="red"
                                    type="link"
                                    className="removeAllFromCartClass text-weigth-bold fw-bold text-center"
                                    icon={<DeleteOutlined/>}
                                    danger
                                    loading={isClearing}
                                    disabled={isClearing}
                                    aria-busy={isClearing}
                            >
                                Vaciar Carrito
                            </Button>
                        </Popconfirm>
                    </div>
                )}
            </Drawer>
        </div>
    );
}
